import { keepPreviousData, useQueries, useQuery } from "@tanstack/react-query";
import { Empty, Select, Spin, Tooltip } from "antd";
import debounce from "lodash/debounce";
import { apiProdutorGet } from "pages/produtores/api/apiProdutorGet";
import { type ApiProdutorGetSearchParams, apiProdutorGetSearch } from "pages/produtores/api/apiProdutorGetSearch";
import type { ApiProdutor } from "pages/produtores/types/ApiProdutor";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { formatDocument } from "std/format";

const QTDE_MIN_CHARS = 3;
const debounceTimeout = 600;
const SELECT_ALL_VALUE = -999999;

type SelectProdutorMultipleProps = {
	value?: number[];
	selectedProdutores?: ApiProdutor[];
	onChange?: (value: number[], produtores: ApiProdutor[]) => void;
	placeholder?: string;
	disabled?: boolean;
	extras?: ApiProdutorGetSearchParams;
	selectAllLabel?: string;
};

function getProdutorLabel(produtor: ApiProdutor): string {
	return produtor?.pessoa?.pes_nome_razaosocial || produtor?.for_codigoindividual?.toString() || String(produtor.for_idpk);
}

function isProdutorHydrated(produtor: ApiProdutor | undefined): boolean {
	return Boolean(produtor?.pessoa?.pes_nome_razaosocial);
}

async function fetchProdutorById(id: number): Promise<ApiProdutor | undefined> {
	const response = await apiProdutorGet({ idpk: id });
	return response.registros?.[0];
}

export function SelectProdutorMultiple({
	value = [],
	selectedProdutores = [],
	onChange,
	placeholder,
	disabled,
	extras,
	selectAllLabel,
}: SelectProdutorMultipleProps) {
	const [searchValue, setSearchValue] = useState("");

	const params: ApiProdutorGetSearchParams = useMemo(
		() => ({
			descricao: searchValue.length >= QTDE_MIN_CHARS ? searchValue : undefined,
			...extras,
		}),
		[searchValue, extras],
	);

	const { data, isFetching } = useQuery({
		queryKey: ["apiProdutorGetSearch", "multiple", params],
		queryFn: () => apiProdutorGetSearch({ params }),
		placeholderData: keepPreviousData,
		staleTime: 5 * 60 * 1000,
	});

	const idsNeedingFetch = useMemo(() => {
		return value.filter((id) => {
			if (id === SELECT_ALL_VALUE) {
				return false;
			}

			const selected = selectedProdutores.find((produtor) => produtor.for_idpk === id);
			return !isProdutorHydrated(selected);
		});
	}, [value, selectedProdutores]);

	const fetchedProdutoresQueries = useQueries({
		queries: idsNeedingFetch.map((id) => ({
			queryKey: ["apiProdutorGet", "multiple", id],
			queryFn: () => fetchProdutorById(id),
			staleTime: 5 * 60 * 1000,
		})),
	});

	const fetchedProdutores = useMemo(() => {
		return fetchedProdutoresQueries.map((query) => query.data).filter((produtor): produtor is ApiProdutor => Boolean(produtor));
	}, [fetchedProdutoresQueries]);

	useEffect(() => {
		if (!onChange || fetchedProdutores.length === 0) {
			return;
		}

		const enrichedMap = new Map(fetchedProdutores.map((produtor) => [produtor.for_idpk, produtor]));
		let changed = false;

		const nextSelected = value.map((id) => {
			if (id === SELECT_ALL_VALUE) {
				return { for_idpk: id, for_codigoindividual: String(id) } as ApiProdutor;
			}

			const enriched = enrichedMap.get(id);
			const current = selectedProdutores.find((produtor) => produtor.for_idpk === id);

			if (enriched && !isProdutorHydrated(current)) {
				changed = true;
				return enriched;
			}

			return current || enriched || ({ for_idpk: id, for_codigoindividual: String(id) } as ApiProdutor);
		});

		if (changed) {
			onChange(value, nextSelected);
		}
	}, [fetchedProdutores, onChange, selectedProdutores, value]);

	const handleSearch = useMemo(() => {
		return debounce((nextValue: string) => {
			setSearchValue(nextValue);
		}, debounceTimeout);
	}, []);

	const handleChange = (nextValue: number[]): void => {
		let selectedIds = nextValue;

		if (selectAllLabel) {
			const withoutAll = nextValue.filter((id) => id !== SELECT_ALL_VALUE);
			const clickedAll = nextValue.includes(SELECT_ALL_VALUE);
			const wasShowingAll = value.length === 0;

			if (nextValue.length === 0 || (clickedAll && !wasShowingAll) || (clickedAll && withoutAll.length === 0)) {
				setSearchValue("");
				onChange?.([], []);
				return;
			}

			selectedIds = withoutAll;
		}

		const searchResults = data || [];
		const nextSelected = selectedIds.map((id) => {
			return (
				searchResults.find((item) => item.for_idpk === id) ||
				fetchedProdutores.find((item) => item.for_idpk === id) ||
				selectedProdutores.find((item) => item.for_idpk === id) ||
				({ for_idpk: id, for_codigoindividual: String(id) } as ApiProdutor)
			);
		});

		onChange?.(selectedIds, nextSelected);
	};

	const options = useMemo(() => {
		const mapped = new Map<number, ApiProdutor>();

		for (const produtor of selectedProdutores) {
			mapped.set(produtor.for_idpk, produtor);
		}

		for (const produtor of fetchedProdutores) {
			mapped.set(produtor.for_idpk, produtor);
		}

		for (const produtor of data || []) {
			mapped.set(produtor.for_idpk, produtor);
		}

		const mappedOptions = Array.from(mapped.values()).map((item) => ({
			value: item.for_idpk,
			label: getProdutorLabel(item),
			produtor: item,
		}));

		if (!selectAllLabel) {
			return mappedOptions;
		}

		return [{ value: SELECT_ALL_VALUE, label: selectAllLabel, produtor: undefined as unknown as ApiProdutor }, ...mappedOptions];
	}, [data, fetchedProdutores, selectAllLabel, selectedProdutores]);

	const renderNotFoundData = (): ReactNode => {
		if (searchValue.length > 0 && searchValue.length < QTDE_MIN_CHARS) {
			return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Digite ao menos 3 caracteres" />;
		}

		return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;
	};

	return (
		<Select
			mode="multiple"
			allowClear={true}
			showSearch={true}
			maxTagCount="responsive"
			placeholder={placeholder || "Selecione um ou mais produtores"}
			filterOption={false}
			disabled={disabled}
			loading={isFetching || fetchedProdutoresQueries.some((query) => query.isFetching)}
			value={selectAllLabel && value.length === 0 ? [SELECT_ALL_VALUE] : value}
			options={options}
			optionLabelProp="label"
			notFoundContent={isFetching ? <Spin size="small" /> : renderNotFoundData()}
			onSearch={handleSearch}
			onChange={handleChange}
			onClear={() => {
				setSearchValue("");
				onChange?.([], []);
			}}
			optionRender={(option) => {
				const produtor = option.data.produtor as ApiProdutor | undefined;

				if (!produtor?.pessoa) {
					return option.label;
				}

				return (
					<Tooltip placement="topLeft" title={produtor.pessoa?.pes_nome_razaosocial || ""}>
						<div>
							<div style={{ fontWeight: "bold" }}>{produtor.pessoa?.pes_nome_razaosocial}</div>
							{produtor.for_codigoindividual ? (
								<div style={{ fontSize: "13px", opacity: "0.7" }}>Código: {produtor.for_codigoindividual}</div>
							) : null}
							{produtor.pessoa?.pes_cnpj_cpf ? (
								<div style={{ fontSize: "13px", opacity: "0.7" }}>CNPJ/CPF: {formatDocument(produtor.pessoa.pes_cnpj_cpf)}</div>
							) : null}
						</div>
					</Tooltip>
				);
			}}
		/>
	);
}
